import * as XLSX from 'xlsx-js-style';

/**
 * Shared helpers for building UI-friendly, right-to-left Excel report sheets.
 *
 * These helpers replace the previous "raw data dump" exports with a report-style
 * worksheet that has:
 *   - a title/context block at the top (merged across the table width)
 *   - a clearly separated, colored header row
 *   - explicit column widths so Hebrew labels and amounts are not clipped
 *   - a right-to-left sheet view so it reads naturally for Hebrew reports
 *   - number formatting, borders, alternating row stripes and highlighted totals
 *
 * We use `xlsx-js-style` (a drop-in fork of `xlsx`) so font/fill/border cell
 * styling is actually written to the file.
 */

const AMOUNT_NUMBER_FORMAT = '#,##0.00';

const PALETTE = {
  primary: 'FF2F4BFF',
  primaryDark: 'FF1E308F',
  headerFont: 'FFFFFFFF',
  stripe: 'FFF6F8FF',
  totalFill: 'FFD7DFFF',
  text: 'FF1F2937',
  border: 'FFC3CBDB',
};

const thinBorder = { style: 'thin', color: { rgb: PALETTE.border } };
const allThin = { top: thinBorder, bottom: thinBorder, left: thinBorder, right: thinBorder };

export const REPORT_STYLES = {
  clientLine: { font: { bold: true, sz: 13, color: { rgb: PALETTE.text } }, alignment: { horizontal: 'right' } },
  reportTitle: { font: { bold: true, sz: 20, color: { rgb: PALETTE.primary } }, alignment: { horizontal: 'center' } },
  subTitle: { font: { bold: true, sz: 12, color: { rgb: PALETTE.text } }, alignment: { horizontal: 'center' } },
  header: {
    font: { bold: true, sz: 12, color: { rgb: PALETTE.headerFont } },
    fill: { fgColor: { rgb: PALETTE.primary } },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: allThin,
  },
  dataText: { font: { sz: 11, color: { rgb: PALETTE.text } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
  dataNum: { font: { sz: 11, color: { rgb: PALETTE.text } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
  stripeText: { font: { sz: 11, color: { rgb: PALETTE.text } }, fill: { fgColor: { rgb: PALETTE.stripe } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
  stripeNum: { font: { sz: 11, color: { rgb: PALETTE.text } }, fill: { fgColor: { rgb: PALETTE.stripe } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
  totalText: { font: { bold: true, sz: 13, color: { rgb: PALETTE.primaryDark } }, fill: { fgColor: { rgb: PALETTE.totalFill } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
  totalNum: { font: { bold: true, sz: 13, color: { rgb: PALETTE.primaryDark } }, fill: { fgColor: { rgb: PALETTE.totalFill } }, alignment: { horizontal: 'right', vertical: 'center' }, border: allThin },
};

const titleStyleForIndex = (index, total) => {
  if (total <= 1) return REPORT_STYLES.reportTitle;
  if (index === 0) return REPORT_STYLES.clientLine;
  if (index === 1) return REPORT_STYLES.reportTitle;
  return REPORT_STYLES.subTitle;
};

/**
 * Build a styled worksheet from an ordered set of columns.
 *
 * @param {Object}   options
 * @param {string[]} [options.titleLines]  Lines shown above the table (each merged across all columns).
 * @param {string[]} options.header        Column header labels.
 * @param {Array<Array<*>>} options.rows   Data rows, aligned with `header`.
 * @param {number[]} [options.colWidths]   Column widths (characters).
 * @param {number[]} [options.numericColIndexes] Indexes of columns that should use the amount number format.
 * @param {number[]} [options.totalRowIndexes]   Indexes (within `rows`) that should be highlighted as totals.
 * @returns {XLSX.WorkSheet}
 */
export const buildReportSheet = ({
  titleLines = [],
  header,
  rows = [],
  colWidths,
  numericColIndexes = [],
  totalRowIndexes = [],
}) => {
  const colCount = header.length;
  const numericSet = new Set(numericColIndexes);
  const totalSet = new Set(totalRowIndexes);

  const aoa = [];
  titleLines.forEach((line) => aoa.push([line]));
  if (titleLines.length) aoa.push([]);
  const headerRowIndex = aoa.length;
  aoa.push(header);
  rows.forEach((row) => aoa.push(row));

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Merge each title line across the full table width so it reads like a header.
  const merges = titleLines.map((_, i) => ({
    s: { r: i, c: 0 },
    e: { r: i, c: Math.max(colCount - 1, 0) },
  }));
  if (merges.length) ws['!merges'] = merges;

  // Column widths.
  ws['!cols'] = header.map((label, c) => {
    if (Array.isArray(colWidths) && colWidths[c] != null) return { wch: colWidths[c] };
    let max = String(label ?? '').length;
    rows.forEach((row) => {
      const v = row[c];
      const len = v == null ? 0 : String(v).length;
      if (len > max) max = len;
    });
    const width = numericSet.has(c) ? Math.max(14, max + 2) : Math.max(12, max + 2);
    return { wch: Math.min(48, width) };
  });

  // Row heights.
  const heights = [];
  titleLines.forEach((_, i) => {
    const style = titleStyleForIndex(i, titleLines.length);
    heights.push({ hpt: style === REPORT_STYLES.reportTitle ? 30 : 22 });
  });
  if (titleLines.length) heights.push({ hpt: 8 }); // spacer
  heights.push({ hpt: 26 }); // header
  rows.forEach((_, i) => heights.push({ hpt: totalSet.has(i) ? 24 : 18 }));
  ws['!rows'] = heights;

  ws['!views'] = [{ RTL: true }];

  // Style title lines (styling on the merge anchor cell only).
  titleLines.forEach((_, i) => {
    const addr = XLSX.utils.encode_cell({ r: i, c: 0 });
    if (ws[addr]) ws[addr].s = titleStyleForIndex(i, titleLines.length);
  });

  // Style header row.
  for (let c = 0; c < colCount; c += 1) {
    const addr = XLSX.utils.encode_cell({ r: headerRowIndex, c });
    let cell = ws[addr];
    if (!cell) { cell = { t: 's', v: '' }; ws[addr] = cell; }
    cell.s = REPORT_STYLES.header;
  }

  // Style data / total rows.
  rows.forEach((_, rowIdx) => {
    const r = headerRowIndex + 1 + rowIdx;
    const isTotal = totalSet.has(rowIdx);
    const isStripe = !isTotal && rowIdx % 2 === 1;
    for (let c = 0; c < colCount; c += 1) {
      const addr = XLSX.utils.encode_cell({ r, c });
      let cell = ws[addr];
      if (!cell) { cell = { t: 's', v: '' }; ws[addr] = cell; }
      const isNum = numericSet.has(c);
      if (isTotal) cell.s = isNum ? REPORT_STYLES.totalNum : REPORT_STYLES.totalText;
      else if (isStripe) cell.s = isNum ? REPORT_STYLES.stripeNum : REPORT_STYLES.stripeText;
      else cell.s = isNum ? REPORT_STYLES.dataNum : REPORT_STYLES.dataText;
      if (isNum && typeof cell.v === 'number') cell.z = AMOUNT_NUMBER_FORMAT;
    }
  });

  return ws;
};

/**
 * Force the workbook (and therefore its sheets) to render right-to-left.
 *
 * NOTE: In xlsx@0.18.x the per-sheet `ws['!views'] = [{ RTL: true }]` flag is
 * NOT honoured by the writer, so the exported file opens left-to-right. Setting
 * the RTL flag at the workbook level DOES emit `rightToLeft="1"` on the sheet
 * views, so we set it here to guarantee the Hebrew reports open RTL.
 */
export const applyWorkbookRtl = (wb) => {
  wb.Workbook = wb.Workbook || {};
  wb.Workbook.Views = wb.Workbook.Views || [];
  wb.Workbook.Views[0] = { ...(wb.Workbook.Views[0] || {}), RTL: true };
  return wb;
};

/** Left-to-right counterpart of {@link applyWorkbookRtl}, for reports exported in LTR languages. */
export const applyWorkbookLtr = (wb) => {
  wb.Workbook = wb.Workbook || {};
  wb.Workbook.Views = wb.Workbook.Views || [];
  wb.Workbook.Views[0] = { ...(wb.Workbook.Views[0] || {}), RTL: false };
  return wb;
};

/**
 * Convenience: build a workbook containing a single report sheet.
 */
export const buildReportWorkbook = ({ sheetName = 'Report', ...sheetOptions }) => {
  const ws = buildReportSheet(sheetOptions);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  applyWorkbookRtl(wb);
  return wb;
};

export { AMOUNT_NUMBER_FORMAT };
